
import { Helmet } from "react-helmet-async";

interface MetaTagsProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  schema?: object;
  image?: string;
  googleVerification?: string;
  noIndex?: boolean;
  isToolPage?: boolean;
}

export const MetaTags = ({ 
  title, 
  description, 
  canonicalUrl, 
  schema,
  image = "/og-image.png",
  googleVerification,
  noIndex = process.env.NODE_ENV !== 'production',
  isToolPage = false
}: MetaTagsProps) => {
  const siteUrl = window.location.origin;
  const currentUrl = window.location.href;
  
  // Récupérer le nonce depuis le document si disponible
  // Cette approche permet de récupérer le nonce généré par le serveur
  const nonce = document.querySelector('script[nonce]')?.getAttribute('nonce') || '';

  // Pour les pages d'outils, on force l'indexation même en environnement de développement
  const shouldIndex = isToolPage ? true : !noIndex;

  // Default schema for all pages
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AlunaTools",
    "url": siteUrl,
    "description": "Suite d'outils marketing et productivité"
  };

  // Schema spécifique pour les pages d'outils
  const toolSchema = isToolPage ? {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": `${title} | AlunaTools`,
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": description
  } : null;

  // Merge with custom schema if provided
  const finalSchema = schema ? schema : (toolSchema || defaultSchema);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title} | AlunaTools</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl || currentUrl} />
      
      {/* Robots directive - noindex for staging/development unless it's a tool page */}
      {!shouldIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Google Verification if provided */}
      {googleVerification && (
        <meta name="google-site-verification" content={googleVerification} />
      )}

      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${siteUrl}${image}`} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${siteUrl}${image}`} />

      {/* JSON-LD Schema - maintenant avec l'attribut nonce */}
      <script type="application/ld+json" nonce={nonce}>
        {JSON.stringify(finalSchema)}
      </script>
    </Helmet>
  );
};
